import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';
import { SessionActivityService } from '../services/session-activity.service';

const AUTH_ENDPOINTS = ['/auth/login', '/auth/logout', '/auth/refresh'];
const USER_ACTIVITY_HEADER = 'X-LV2-User-Activity';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const sessionActivity = inject(SessionActivityService);
  const accessToken = authService.getToken();
  const hasUserActivity = shouldSignalActivity(req, accessToken) && sessionActivity.claimActivitySignal();
  const request = addAuthorization(addUserActivity(req, hasUserActivity), accessToken);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (!shouldRefresh(req, error)) {
        return throwError(() => error);
      }

      const currentAccessToken = authService.getToken();
      if (!accessToken || !currentAccessToken) {
        return throwError(() => error);
      }

      if (accessToken !== currentAccessToken) {
        return next(addAuthorization(request, currentAccessToken));
      }

      return from(authService.refreshAccessToken(accessToken)).pipe(
        switchMap((token) => next(addAuthorization(request, token))),
      );
    }),
  );
};

function shouldRefresh(request: HttpRequest<unknown>, error: unknown): boolean {
  const payload = error instanceof HttpErrorResponse ? (error.error as { code?: unknown } | null) : null;

  return (
    error instanceof HttpErrorResponse &&
    error.status === 401 &&
    payload?.code === 'INVALID_ACCESS_TOKEN' &&
    isApiRequest(request) &&
    !isAuthenticationRequest(request)
  );
}

function addAuthorization<T>(request: HttpRequest<T>, token: string | null): HttpRequest<T> {
  if (!token || !isApiRequest(request) || isAuthenticationRequest(request)) {
    return request;
  }

  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function addUserActivity<T>(request: HttpRequest<T>, hasUserActivity: boolean): HttpRequest<T> {
  if (!hasUserActivity) {
    return request;
  }

  return request.clone({ setHeaders: { [USER_ACTIVITY_HEADER]: '1' } });
}

function shouldSignalActivity(request: HttpRequest<unknown>, accessToken: string | null): boolean {
  return accessToken !== null && isApiRequest(request) && !isAuthenticationRequest(request);
}

function isAuthenticationRequest(request: HttpRequest<unknown>): boolean {
  return AUTH_ENDPOINTS.some((url) => request.url.includes(url));
}

function isApiRequest(request: HttpRequest<unknown>): boolean {
  const browserOrigin = typeof globalThis.location === 'undefined' ? 'http://localhost' : globalThis.location.origin;
  const apiUrl = new URL(environment.apiUrl, `${browserOrigin}/`);
  const requestUrl = new URL(request.url, `${browserOrigin}/`);

  if (requestUrl.origin !== apiUrl.origin) {
    return false;
  }

  const apiPath = apiUrl.pathname.replace(/\/$/, '');
  return apiPath === '' || requestUrl.pathname === apiPath || requestUrl.pathname.startsWith(`${apiPath}/`);
}
